import { Location, TitleCasePipe } from '@angular/common';
import { Component, effect, inject, input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ReimbursementTypes } from '@app/enums/ReimbursementTypes';
import { RequestTypes } from '@app/enums/RequestTypes';
import { ISaveStatus } from '@app/generated-api';
import { ChangeRequestStatusRequest } from '@app/generated-api/models/ChangeRequestStatusRequest';
import { MessageDialogComponent } from '@app/v2/components/dialogs/message-dialog/message-dialog.component';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { RequestService } from '@app/v2/services/request/request.service';
import { StipendRequestService } from '@app/v2/services/stipend-request/stipend-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Patient } from '@models/patient';
import { Study, VisitName } from '@models/study';
import { FieldValidator } from '@models/validators/field-validator';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { RequestContextState, RequestStateService } from '@services/state-management/request-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import {
    transformToReimbursementRequest,
    transformToStipendRequest,
    transformToTravelRequest,
} from '@services/transforms/request-transform';
import { UserService } from '@services/user/user.service';
import { ModalSize } from '@utility/modal-helper';
import { PageMode, RequestPageMode } from '@utility/utility';
import { combineLatest, EMPTY, filter, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import {
    GenericRequest,
    GenericRequestStatus,
    PaymentRequestStatus,
    ReimbursementRequest,
    RequestStatus,
    RequestStatusNames,
    StipendRequest,
    TravelAccommodations,
    TravelRequest,
    TravelRequestStatus,
} from '../../models/request';
import { Site, SiteServices } from '../../models/site';

@Component({
    selector: '',
    standalone: false,
    templateUrl: './create-edit-request.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [ReimbursementRequestService, StipendRequestService, TitleCasePipe], // must be provided page-wise, not a singleton, because it provides state that should be cleared page changes
})
export class MedpaceCreateEditPaymentTravelRequestComponent implements OnInit, OnDestroy {
    private readonly titleCase = inject(TitleCasePipe);
    private readonly requestServiceV2 = inject(RequestService);

    request: GenericRequest;
    patient: Patient;
    study: Study;
    visitTypes: VisitName[];
    site: Site;
    siteServiceInfo: SiteServices;
    isUpdate: boolean = false;
    validationArray: FieldValidator<any>[];
    elevatedAccess$ = this.userServiceV2.permissions.request.access.elevated$;
    stipendOrOOP: string;
    changedPaymentType: boolean = false;
    private componentDestroyed$: Subject<boolean> = new Subject();
    destroy$ = new Subject();
    private cancelGetNewRequestState$ = new Subject<void>();

    constructor(
        private activeRoute: ActivatedRoute,
        private router: Router,
        private requestServices: AdminRequestServices,
        public dialog: MatDialog,
        private snackbarService: SnackbarService,
        private requestStateService: RequestStateService,
        private studyStateService: StudyStateService,
        private siteStateService: SiteStateService,
        private patientStateService: PatientStateService,
        private userService: UserService,
        private userServiceV2: UserServiceV2,
        private location: Location
    ) {}

    setStates() {
        this.elevatedAccess$
            .pipe(
                tap((elevatedAccess) => {
                    this.studyStateService.setStudy(this.activeRoute.snapshot.params.id);
                    this.patientStateService.setSummaryPatientData(this.activeRoute.snapshot.params.patientId);
                    this.requestStateService.setVisits(this.activeRoute.snapshot.params.id);
                    elevatedAccess
                        ? this.siteStateService.setSiteAndStudyUsers(this.activeRoute.snapshot.params.siteId)
                        : this.siteStateService.setSite(this.activeRoute.snapshot.params.siteId);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();
    }

    getStates() {
        let study = this.studyStateService.getStudy();
        let patient = this.patientStateService.getSummaryPatientData();
        let site = this.siteStateService.getSite();
        let visits = this.requestStateService.getVisits();

        combineLatest({ study, patient, site, visits })
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((result) => {
                    this.study = result.study;
                    this.patient = result.patient;

                    this.site = result.site;
                    if (result.site) {
                        this.siteServiceInfo = result.site.services;
                        if (this.patient) this.patient.patientIdentification['siteId'] = this.site.id;
                    }

                    if (this.patient?.patientIdentification && this.study)
                        this.patient.patientIdentification['studyId'] = this.study.id;

                    this.visitTypes = result.visits;
                    if (this.study) this.study.studyInfo.visitTypes = this.visitTypes;
                })
            )
            .subscribe();
    }
    clearStore() {
        this.requestStateService.clearStore();
        this.requestStateService.clearState();
        this.siteStateService.clearStore();
        this.studyStateService.clearStore();
        this.patientStateService.clearStore();
    }
    ngOnInit(): void {
        // clear store on page load to ensure no data from a previous request is shown while loading this request, this can be removed when we move to signals and each component only subscribes to the data it needs
        this.clearStore();
        const payOrTravel = this.IsPaymentOrTravelType();
        this.stipendOrOOP = this.activeRoute.snapshot.queryParams['stipend-oop'];
        this.setStates();
        this.getStates();

        const patientId = this.activeRoute.snapshot.params.patientId;
        if (RequestPageMode(this.activeRoute) === PageMode.CREATE) {
            this.request = <GenericRequest>{
                patientId: patientId,
                type: payOrTravel,
                travelRequests: <TravelAccommodations>{},
                status: <RequestStatus>{
                    statusId: GenericRequestStatus.New,
                    statusName: GenericRequestStatus[GenericRequestStatus.New],
                },
            };
            this.requestStateService.setNewRequest(this.request);
            // set empty state if creating a new request
            this.requestStateService.setState(<RequestContextState>{
                pageMode: PageMode.CREATE,
                requestType: null,
                reimbursementType: null,
                requestStatus: this.request.status,
                patientId: patientId,
            });
            this.requestStateService.travelRequestStateService.setState(null);
            this.requestStateService.stipendRequestStateService.setState(null);
            this.requestStateService.reimbursementRequestStateService.setState(null);
        }

        if (RequestPageMode(this.activeRoute) === PageMode.EDIT) {
            this.isUpdate = true;
            this.GetNewRequestState(payOrTravel);
        }
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();

        this.clearStore();

        this.cancelGetNewRequestState$.next();
        this.cancelGetNewRequestState$.complete();
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    updateAnyRequestPart(event: GenericRequest) {
        if (event?.paymentType !== this.stipendOrOOP) {
            this.changedPaymentType = true;
        }
        if (event.type === 'travel') {
            const tr = transformToTravelRequest(event);

            this.requestServices
                .updateTravelRequest(tr.id, tr)
                .pipe(
                    this.withDuplicateCheck(() => {
                        event.flaggedDuplicate = true;
                        this.updateAnyRequestPart(event);
                    }),
                    switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
                )
                .subscribe({
                    next: (result) => {
                        const payOrTravel = this.IsPaymentOrTravelType();
                        this.GetNewRequestState(payOrTravel);

                        this.requestStateService.setRequest(
                            RequestTypes.TRAVEL,
                            this.stipendOrOOP,
                            this.activeRoute.snapshot.params.requestId
                        );
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'travel'));
                    },
                });
        } else if (event.type === 'payment' && event.paymentType === 'stipend') {
            const stipend = transformToStipendRequest(event);
            this.requestServices
                .updateStipendRequest(stipend)
                .pipe(
                    this.withDuplicateCheck(() => {
                        event.flaggedDuplicate = true;
                        this.updateAnyRequestPart(event);
                    }),
                    switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
                )
                .subscribe({
                    next: (result) => {
                        if (!this.changedPaymentType) {
                            const payOrTravel = this.IsPaymentOrTravelType();
                            this.GetNewRequestState(payOrTravel);

                            this.requestStateService.setRequest(
                                RequestTypes.PAYMENT,
                                this.stipendOrOOP,
                                this.activeRoute.snapshot.params.requestId
                            );
                        } else {
                            this.requestStateService.setRequest(
                                RequestTypes.PAYMENT,
                                'stipend',
                                parseInt(result.infoLabel)
                            );
                            this.setNewRequestUrl(event, result);
                        }
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'stipend'));
                    },
                });
        } else if (event.type === 'payment' && event.paymentType === 'out-of-pocket') {
            const oop = transformToReimbursementRequest(event);

            this.requestServices
                .updateReimbursementRequest(oop.id, oop)
                .pipe(
                    this.withDuplicateCheck(() => {
                        event.flaggedDuplicate = true;
                        this.updateAnyRequestPart(event);
                    }),
                    switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
                )
                .subscribe({
                    next: (result) => {
                        if (!this.changedPaymentType) {
                            const payOrTravel = this.IsPaymentOrTravelType();
                            this.GetNewRequestState(payOrTravel);

                            this.requestStateService.setRequest(
                                RequestTypes.PAYMENT,
                                this.stipendOrOOP,
                                this.activeRoute.snapshot.params.requestId
                            );
                        } else {
                            this.requestStateService.setRequest(
                                RequestTypes.PAYMENT,
                                'out-of-pocket',
                                parseInt(result.infoLabel)
                            );
                            this.setNewRequestUrl(event, result);
                        }
                        this.snackbarService.openInfoSnackbar(
                            this.GetSnackbarMessageContent('update', 'reimbursement')
                        );
                    },
                });
        }
    }

    setNewRequestUrl(event: GenericRequest, result: any) {
        let route: string;
        if (!!result?.infoLabel && !isNaN(parseInt(result?.infoLabel))) {
            const newRequestNumber = parseInt(result?.infoLabel);
            const paymentType = event.paymentType === 'stipend' ? 'stipend' : 'out-of-pocket';
            route = `studies/${this.study.id}/sites/${this.site.id}/patients/${event.patientId}/requests/${newRequestNumber}/edit?payment-travel=payment&stipend-oop=${paymentType}`;
        } else {
            route = `studies/${this.study.id}/sites/${this.site.id}/patients/${event.patientId}/overview`;
        }
        this.router.navigateByUrl(route).then((navigated: boolean) => {
            if (navigated) {
                this.snackbarService.openInfoSnackbar(
                    this.GetSnackbarMessageContent(
                        'update',
                        event.paymentType === 'stipend' ? 'stipend' : 'reimbursement'
                    )
                );
            }
        });
    }

    saveDraftRequest(event: GenericRequest) {
        if (event.type === 'travel') {
            const tr = transformToTravelRequest(event);
            if (event.id) {
                this.requestServices
                    .updateTravelRequest(tr.id, tr)
                    .pipe(
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (result) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'travel'));
                        },
                    });
            } else {
                this.requestServices
                    .createTravelRequest(tr)
                    .pipe(
                        map((response) => response.body),
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (response) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('create', 'travel'));
                        },
                    });
            }
        } else if (event.type === 'payment' && event.paymentType === 'stipend') {
            const stipend = transformToStipendRequest(event);
            if (event.id) {
                this.requestServices
                    .updateStipendRequest(stipend)
                    .pipe(
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (result) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'stipend'));
                        },
                    });
            } else {
                this.requestServices
                    .createStipendRequest(stipend)
                    .pipe(
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (response) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('create', 'stipend'));
                        },
                    });
            }
        } else if (event.type === 'payment' && event.paymentType === 'out-of-pocket') {
            const oop = transformToReimbursementRequest(event);

            if (event.id) {
                this.requestServices
                    .updateReimbursementRequest(oop.id, oop)
                    .pipe(
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (result) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(
                                this.GetSnackbarMessageContent('update', 'reimbursement')
                            );
                        },
                    });
            } else {
                // TODO: Confirm that this is only used for Draft creation
                this.requestServices
                    .createReimbursementRequest(oop)
                    .pipe(
                        map((response) => response.body),
                        this.withDuplicateCheck(() => {
                            event.flaggedDuplicate = true;
                            this.saveDraftRequest(event);
                        }),
                        switchMap((result) =>
                            this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result))
                        )
                    )
                    .subscribe({
                        next: (response) => {
                            this.location.back();
                            this.snackbarService.openInfoSnackbar(
                                this.GetSnackbarMessageContent('create', 'reimbursement')
                            );
                        },
                    });
            }
        }
    }
    submitRequest(event: GenericRequest) {
        const origStatus = event.status;
        this.request = event;
        this.executeSaveRequest(this.request, origStatus, null);
    }

    updateRequestStatus(event: ChangeRequestStatusRequest) {
        const redirectUrl = `studies/${this.study.id}/sites/${this.site.id}/patients/${this.patient.id}/overview`;

        this.requestServiceV2
            .changeStatus(event)
            .pipe(
                take(1),
                tap(() => {
                    this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                        if (navigated) {
                            this.snackbarService.openInfoSnackbar(
                                `Request status has been successfully changed to: ${RequestStatusNames[event.newStatus]}`
                            );
                        }
                    });
                })
            )
            .subscribe();
    }

    private updateTravelRequest(
        travelRequest: TravelRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .updateTravelRequest(travelRequest.id, travelRequest)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('travel');

                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('update', 'travel')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'travel'));
                    }
                },
            });
    }

    private updateStipendRequest(
        stipend: StipendRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .updateStipendRequest(stipend)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('payment');
                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('update', 'stipend')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'stipend'));
                    }
                },
            });
    }

    private updateReimbursementRequest(
        oop: ReimbursementRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .updateReimbursementRequest(oop.id, oop)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('payment');
                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('update', 'reimbursement')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(
                            this.GetSnackbarMessageContent('update', 'reimbursement')
                        );
                    }
                },
            });
    }

    private submitStipendRequest(stipend: StipendRequest, request: GenericRequest, fallbackStatus: RequestStatus) {
        this.requestServices
            .submitStipendRequest(stipend)
            .pipe(
                map((response) => response.body),
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (response) => {
                    if (stipend.id) {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'stipend'));
                    } else {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('create', 'stipend'));
                    }
                },
            });
    }

    private submitReimbursementRequest(
        oop: ReimbursementRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus
    ) {
        this.requestServices
            .submitReimbursementRequest(oop)
            .pipe(
                map((response) => response.body),
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (response) => {
                    if (oop.id) {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(
                            this.GetSnackbarMessageContent('update', 'reimbursement')
                        );
                    } else {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(
                            this.GetSnackbarMessageContent('create', 'reimbursement')
                        );
                    }
                },
            });
    }

    private submitTravelRequest(travelRequest: TravelRequest, request: GenericRequest, fallbackStatus: RequestStatus) {
        this.requestServices
            .submitTravelRequest(travelRequest)
            .pipe(
                map((response) => response.body),
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (response) => {
                    if (travelRequest.id) {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('update', 'travel'));
                    } else {
                        this.location.back();
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('create', 'travel'));
                    }
                },
            });
    }

    private completeReimbursementRequest(
        oop: ReimbursementRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .completeReimbursementRequest(oop.id, oop)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('payment');
                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('complete', 'reimbursement')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(
                            this.GetSnackbarMessageContent('complete', 'reimbursement')
                        );
                    }
                },
            });
    }

    private completeStipendRequest(
        stipend: StipendRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .completeStipendRequest(stipend)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('payment');

                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('complete', 'stipend')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('complete', 'stipend'));
                    }
                },
            });
    }

    private completeTravelRequest(
        travelRequest: TravelRequest,
        request: GenericRequest,
        fallbackStatus: RequestStatus,
        redirectUrl: string | null
    ) {
        this.requestServices
            .completeTravelRequest(travelRequest.id, travelRequest)
            .pipe(
                this.withDuplicateCheck(
                    () => {
                        request.flaggedDuplicate = true;
                        this.submitRequest(request);
                    },
                    () => {
                        request.status = fallbackStatus;
                    }
                ),
                switchMap((result) => this.requestServiceV2.submittedRequestsCount.get$().pipe(map((_) => result)))
            )
            .subscribe({
                next: (result) => {
                    this.GetNewRequestState('travel');

                    if (redirectUrl) {
                        this.router.navigate([redirectUrl]).then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar(
                                    this.GetSnackbarMessageContent('complete', 'travel')
                                );
                            }
                        });
                    } else {
                        this.snackbarService.openInfoSnackbar(this.GetSnackbarMessageContent('complete', 'travel'));
                    }
                },
            });
    }

    executeSaveRequest(request: GenericRequest, fallbackStatus: RequestStatus, redirectUrl: string | null) {
        switch (request.type) {
            case RequestTypes.TRAVEL:
                const tr = transformToTravelRequest(request);

                switch (tr.status.statusId) {
                    case TravelRequestStatus.New:
                    case TravelRequestStatus.Draft:
                    case TravelRequestStatus.PendingSiteVerification:
                        this.submitTravelRequest(tr, request, fallbackStatus);
                        break;
                    case TravelRequestStatus.PendingInvoice: //complete
                        this.completeTravelRequest(tr, request, fallbackStatus, redirectUrl);
                        break;
                    default:
                        this.updateTravelRequest(tr, request, fallbackStatus, redirectUrl);
                        break;
                }
                break;
            case RequestTypes.PAYMENT:
                switch (request.paymentType) {
                    case RequestTypes.STIPEND:
                        const stipend = transformToStipendRequest(request);
                        switch (stipend.status.statusId) {
                            case PaymentRequestStatus.New:
                            case PaymentRequestStatus.Draft:
                            case PaymentRequestStatus.PendingSiteVerification:
                                this.submitStipendRequest(stipend, request, fallbackStatus);
                                break;
                            case PaymentRequestStatus.PendingERSubmission:
                                this.completeStipendRequest(stipend, request, fallbackStatus, redirectUrl);
                                break;
                            default:
                                this.updateStipendRequest(stipend, request, fallbackStatus, redirectUrl);
                                break;
                        }
                        break;
                    case RequestTypes.REIMBURSEMENT:
                        const oop = transformToReimbursementRequest(request);

                        switch (oop.status.statusId) {
                            case PaymentRequestStatus.New:
                            case PaymentRequestStatus.Draft:
                            case PaymentRequestStatus.PendingSiteVerification:
                                this.submitReimbursementRequest(oop, request, fallbackStatus);
                                break;
                            case PaymentRequestStatus.PendingERSubmission:
                                this.completeReimbursementRequest(oop, request, fallbackStatus, redirectUrl);
                                break;
                            default:
                                this.updateReimbursementRequest(oop, request, fallbackStatus, redirectUrl);
                                break;
                        }
                        break;
                }
        }
    }
    patientNavAction(event: any) {
        if (event.target === 'profile') {
            //goto profile
            this.router.navigate(
                [`studies/${this.site.studyId}/sites/${this.site.id}/patients/${this.patient.id}/edit`],
                { state: this.patient }
            );
        }
        if (event.target === 'activity') {
            //goto activity
            this.router.navigate(
                [`studies/${this.site.studyId}/sites/${this.site.id}/patients/${this.patient.id}/overview`],
                {
                    state: this.patient,
                }
            );
        }
    }

    private showDuplicateConfirmDialog(): Observable<string> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.maxWidth = ModalSize.Small;
        dialogConfig.minWidth = ModalSize.Small;
        dialogConfig.data = {
            title: 'Potential Duplicate',
            bodyText:
                'A request with the same information already exists for this patient. Do you still want to submit?',
            showCancelButton: true,
            okayButtonLabel: 'Continue',
            cancelButtonLabel: 'Cancel',
        };
        return this.dialog.open(MessageDialogComponent, dialogConfig).afterClosed();
    }

    private withDuplicateCheck(onConfirm: () => void, onCancel?: () => void) {
        return (source: Observable<ISaveStatus>) =>
            source.pipe(
                switchMap((result) => {
                    if (result?.errorReasonCode === 'DuplicateRequest') {
                        return this.showDuplicateConfirmDialog().pipe(
                            switchMap((dialogResult) => {
                                if (dialogResult?.toLowerCase() === 'okay') {
                                    onConfirm();
                                } else {
                                    onCancel?.();
                                }
                                return EMPTY;
                            })
                        );
                    }
                    return of(result);
                })
            );
    }

    private GetNewRequestState(payOrTravel: 'payment' | 'travel') {
        this.cancelGetNewRequestState$.next(); // cancel any previous pending subscription
        this.requestStateService.clearState(); // clear previous request state
        const requestId = +this.activeRoute.snapshot.params.requestId;
        this.requestStateService.setRequest(payOrTravel, this.stipendOrOOP, requestId);
        this.requestStateService
            .getRequest()
            .pipe(
                filter((request) => !!request && request.id === requestId), // unknown origin error - previous requests are sometimes emitted, quickfix is to filter out other request ids
                take(1),
                takeUntil(this.cancelGetNewRequestState$), // cancel if GetNewRequestState is called again before this resolves
                takeUntil(this.destroy$) // cancel if component is destroyed before this resolves
            )
            .subscribe((request) => {
                this.request = request;
                const requestType =
                    payOrTravel === 'payment'
                        ? RequestTypes.PAYMENT
                        : payOrTravel === 'travel'
                          ? RequestTypes.TRAVEL
                          : null;
                const reimbursementType =
                    this.stipendOrOOP === 'stipend'
                        ? ReimbursementTypes.Stipend
                        : this.stipendOrOOP === 'out-of-pocket'
                          ? ReimbursementTypes.OutOfPocket
                          : null;
                // populate state with the appropriate request from api when editing request
                this.requestStateService.setState(<RequestContextState>{
                    pageMode: PageMode.EDIT,
                    requestType: requestType,
                    reimbursementType: reimbursementType,
                    requestStatus: this.request.status,
                });
                if (requestType === RequestTypes.TRAVEL) {
                    this.requestStateService.travelRequestStateService.populateState(request.id);
                    this.requestStateService.stipendRequestStateService.setState(null);
                    this.requestStateService.reimbursementRequestStateService.setState(null);
                } else if (requestType === RequestTypes.PAYMENT && reimbursementType === ReimbursementTypes.Stipend) {
                    this.requestStateService.travelRequestStateService.setState(null);
                    this.requestStateService.stipendRequestStateService.populateState(request.id);
                    this.requestStateService.reimbursementRequestStateService.setState(null);
                } else if (
                    requestType === RequestTypes.PAYMENT &&
                    reimbursementType === ReimbursementTypes.OutOfPocket
                ) {
                    this.requestStateService.travelRequestStateService.setState(null);
                    this.requestStateService.stipendRequestStateService.setState(null);
                    this.requestStateService.reimbursementRequestStateService.populateState(request.id);
                }
            });

        if (payOrTravel === 'payment') {
            if (this.stipendOrOOP === 'out-of-pocket') {
                this.reimbursementRequestService.get(+requestId).subscribe();
            } else if (this.stipendOrOOP === 'stipend') {
                this.stipendRequestService.get(+requestId).subscribe();
            }
        }
    }

    private IsPaymentOrTravelType() {
        return this.activeRoute.snapshot.queryParams['payment-travel'];
    }

    getTitleLabel(): string {
        return this.isUpdate ? 'Edit Request' : 'Create New Request';
    }

    private GetSnackbarMessageContent(action: 'create' | 'update' | 'complete', requestType: string): string {
        const actionString = action === 'create' ? `${action}d new` : `${action}d`;
        return this.titleCase.transform(`${actionString} ${requestType} request`);
    }

    reimbursementRequestService = inject(ReimbursementRequestService);
    stipendRequestService = inject(StipendRequestService);
    id = input<string>(); // studyId
    siteId = input<string>();
    patientId = input<string>();
    requestId = input<string | undefined>();

    // eslint-disable-next-line
    paymentTravel = input<'payment' | 'travel' | undefined>(undefined, { alias: 'payment-travel' });
    // eslint-disable-next-line
    stipendOOP = input<'stipend' | 'out-of-pocket' | undefined>(undefined, { alias: 'stipend-oop' });
    setRequest = effect(() => {
        const studyId = this.id();
        const siteId = this.siteId();
        const patientId = this.patientId();
        const requestId = this.requestId();
        const paymentTravel = this.paymentTravel();
        const stipendOOP = this.stipendOOP();

        if (!studyId || !siteId || !patientId || !requestId || !paymentTravel || !stipendOOP) return;

        if (paymentTravel === 'payment') {
            if (stipendOOP === 'out-of-pocket') {
                this.reimbursementRequestService.get(+requestId).subscribe();
            } else {
                this.stipendRequestService.get(+requestId).subscribe();
            }
        }
    });
}
